import { BehaviorSubject, Subject } from 'rxjs';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AsyncState } from '@peca/backoffice/models/async-state.model';
import { AutocompleteItemSource, BreadcrumbItem, MultipleItem, Pagination } from '@peca/design-system';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService, PersistQueryService } from '@peca/commons';
import { Pageable } from '@peca/backoffice/models/pageable.model';
import { PurchaseOrderQuery } from '../../../models/purchase-order/purchase-order-query.model';
import { PurchaseOrder } from '../../../models/purchase-order/purchase-order.model';
import { PurchaseOrderService } from '../../../services/purchase.service';
import { OrderPurchase } from '../../../../orders/models/order-purchase.model';
import { PurchaseOrderStatusValues } from '../../../values/status.value';
import { PurchaseOrderStatusPipe } from '../../../pipes/purchase-order-status.pipe';

@Component({
  templateUrl: './purchase-order-list.component.html',
  styleUrls: ['./purchase-order-list.component.scss'],
  standalone: false,
})
export class PurchaseOrderListComponent implements OnInit {
  breadcrumb: BreadcrumbItem[];
  form: FormGroup;
  orders$: BehaviorSubject<AsyncState<PurchaseOrder[]>>;
  lastQuery: PurchaseOrderQuery;
  pagination$: Subject<Pagination>;
  service: PurchaseOrderService;
  router: Router;
  route: ActivatedRoute;
  toast: ToastService;
  persistQueryService: PersistQueryService;
  branches$: BehaviorSubject<AutocompleteItemSource>;
  private readonly searchFormInitialValue = {
    commerceReference: '',
    branch: null,
    status: [],
  };
  status: MultipleItem[];

  constructor() {
    this.breadcrumb = [{ label: 'Compras', path: '/compras' }, { label: 'Pedidos de Compra' }];
    this.form = new FormGroup({ commerceReference: new FormControl(''), branch: new FormControl(''), status: new FormControl([]) });
    this.orders$ = new BehaviorSubject<AsyncState<PurchaseOrder[]>>({ loading: true });
    this.lastQuery = { page: 1, size: 12, sort: 'audit.createdAt,desc' } as PurchaseOrderQuery;
    this.pagination$ = new Subject<Pagination>();
    this.status = this.getStatusSource();
    this.service = inject(PurchaseOrderService);
    this.router = inject(Router);
    this.route = inject(ActivatedRoute);
    this.toast = inject(ToastService);
    this.persistQueryService = inject(PersistQueryService);
    this.branches$ = new BehaviorSubject<AutocompleteItemSource>({});
  }

  get query() {
    const persitedQuery = this.persistQueryService.read<PurchaseOrderQuery>('peca-purchase-order-list');
    const query = persitedQuery || this.lastQuery;
    if (query?.page === 0) query.page = 1;
    delete query.branch;
    return query;
  }

  get formValues() {
    return {
      commerceReference: this.form.get('commerceReference')?.value,
      branch: this.form.get('branch')?.value,
      status: this.form.get('status')?.value,
    };
  }

  ngOnInit() {
    this.form.valueChanges.subscribe(() => this.checkIfSearchIsPrestine());
    this.route.queryParams.subscribe((params) => {
      if (!params['q']) return;
      const { commerceReference, branch, status } = JSON.parse(atob(params['q']));
      this.form.patchValue({
        commerceReference: commerceReference || '',
        branch: branch || null,
        status: status || [],
      });
    });
    const persitedQuery = this.persistQueryService.read<PurchaseOrderQuery>('peca-purchase-order-list');
    this.persistQueryService.rewriteQueryString('peca-purchase-order-list', persitedQuery);
    this.fetchOrders(this.query);
  }

  fetchOrders(query: PurchaseOrderQuery) {
    this.lastQuery = query;
    this.orders$.next({ loading: true });
    this.service.fetchOrders(query).subscribe({
      next: (page: Pageable<PurchaseOrder>) => {
        this.paginate(page);
        this.orders$.next({ data: page.items });
      },
      error: () => {
        this.orders$.next({ failure: true });
      },
    });
  }

  paginate(page: Pageable<unknown>) {
    const pagination = {
      page: page.currentPage,
      size: page.pageSize,
      total: page.totalPages,
      items: page.totalItems,
    };
    this.pagination$.next({ ...pagination, page: pagination.page });
  }

  onSubmit() {
    const persistQuery = {
      ...this.lastQuery,
      ...this.formValues,
      branchId: this.formValues?.branch?.id,
      page: 1,
    };
    this.persistQueryService.rewriteQueryString('peca-purchase-order-list', persistQuery);

    delete persistQuery.branch;

    this.lastQuery = persistQuery;
    this.fetchOrders(persistQuery);
  }

  clearFilters() {
    this.form.reset(this.searchFormInitialValue);
    this.onSubmit();
  }

  checkIfSearchIsPrestine() {
    const currentValue = this.form.value;
    const isFormEmpty = JSON.stringify(currentValue) === JSON.stringify(this.searchFormInitialValue);
    if (isFormEmpty) return this.form.markAsPristine();
    this.form.markAsDirty();
  }

  onPaginate(pagination: Pagination) {
    const page = pagination.page ? pagination.page : 1;
    const query = { ...this.lastQuery, ...pagination, page };
    this.fetchOrders(query);

    const persistQuery = this.persistQueryService.read<PurchaseOrderQuery>('peca-purchase-order-list');
    if (persistQuery?.branch) query.branch = persistQuery.branch;
    this.persistQueryService.rewriteQueryString('peca-purchase-order-list', query);
  }

  hasNFe(order: PurchaseOrder): boolean {
    return !!order?.invoice?.id;
  }

  onClickDetails(order: OrderPurchase) {
    this.router.navigate(['compras', 'pedidos-de-compra', order.id]);
  }

  onClickDownloadNFe(purchase: OrderPurchase) {
    this.service.downloadNFe(purchase.order!.id, purchase.id).subscribe({
      next: (url: unknown | null) => {
        if (!url || typeof url !== 'string') {
          this.toast.failure('Este pedido ainda não possúi uma NFe.');
          return;
        }

        window.open(url);
      },
      error: () => {
        this.toast.failure('Não foi possível baixar a NFe.');
      },
    });
  }

  getStatusSource() {
    const statusPipe = new PurchaseOrderStatusPipe();
    return PurchaseOrderStatusValues.map((status) => ({ id: status, label: statusPipe.transform(status) }));
  }

  onAutocompleteBranches(search: string) {
    if (search.length < 3) return;

    this.branches$.next({ loading: true });
    this.service.suggestSellersBranches(search).subscribe({
      next: (data) => this.branches$.next({ data }),
      error: () => this.branches$.next({ failure: true }),
    });
  }
}
